import React from "react";
import { Box, Typography } from "@mui/material";
import { toast } from "react-toastify";
import GenericFormDialog, { Field } from "../GenericFormDialog";
import { purchasePackage } from "../../services/PackagesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatTableDate } from "../../utils/tableDateFormat";
import { SessionPackage } from "./types";
import { formatMoney } from "./classUtils";
import { Option, useClientOptions, useUnitOptions } from "./useClassOptions";
import PackagePaymentPromptDialog from "./PackagePaymentPromptDialog";

interface PackagePurchaseDialogProps {
  open: boolean;
  businessId?: string;
  sessionPackage: SessionPackage | null;
  onClose: () => void;
  onPurchased?: () => void;
}

/**
 * Purchasing charges the client immediately for the package price and creates
 * their redeemable quota, so the summary spells out what they are being billed.
 */
const PackagePurchaseDialog: React.FC<PackagePurchaseDialogProps> = ({
  open,
  businessId,
  sessionPackage,
  onClose,
  onPurchased,
}) => {
  const [submitting, setSubmitting] = React.useState(false);
  const [paymentPrompt, setPaymentPrompt] = React.useState<{
    invoiceId: string | number | null;
    amount: number;
  } | null>(null);
  const { options: clientOptions, onSearchInput: onClientSearchInput } =
    useClientOptions(businessId, open);
  const { options: unitOptions } = useUnitOptions(businessId, open);

  const fields: Field[] = React.useMemo(() => {
    const summaryField: Field = {
      name: "summary",
      label: "",
      colSpan: 2,
      customRender: () => (
        <Box
          sx={{
            border: "1px solid #eee",
            borderRadius: 2,
            p: 1.5,
            mb: 1,
            bgcolor: "#fafafa",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            {sessionPackage?.name || "Package"}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#555" }}>
            {`${sessionPackage?.session_count ?? "-"} sessions · valid ${
              sessionPackage?.validity_days ?? "-"
            } days · ${formatMoney(sessionPackage?.price)}`}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#777", mt: 0.5 }}>
            {sessionPackage?.scope_type === "CATEGORY"
              ? `Covers: ${sessionPackage?.category?.name || "one category"}`
              : "Covers: any class"}
          </Typography>
        </Box>
      ),
    };

    const clientField: Field = {
      name: "client_id",
      label: "Client",
      type: "dropdown",
      options: clientOptions as { label: string; value: string }[],
      searchable: true,
      onSearchInput: onClientSearchInput,
      required: true,
      colSpan: 2,
    };

    const unitField: Field = {
      name: "unit_id",
      label: "Unit (optional)",
      type: "dropdown",
      options: unitOptions as Option[],
      searchable: true,
      colSpan: 2,
    };

    return [summaryField, clientField, unitField];
  }, [clientOptions, onClientSearchInput, sessionPackage, unitOptions]);

  const initialValues = React.useMemo(
    () => ({ client_id: "", unit_id: "" }),
    []
  );

  const handleSubmit = async (values: Record<string, any>) => {
    if (!sessionPackage || !businessId) return;

    setSubmitting(true);
    try {
      const result = await purchasePackage(String(sessionPackage.id), {
        client_id: String(values.client_id),
        business_id: String(businessId),
        unit_id: values.unit_id ? String(values.unit_id) : undefined,
      });

      const remaining = result?.sessions_remaining ?? sessionPackage.session_count;
      const expiry = result?.expires_at
        ? ` · expires ${formatTableDate(result.expires_at)}`
        : "";
      toast.success(`Package purchased — ${remaining} sessions available${expiry}`);
      onClose();
      // The purchase is UNPAID until settled — offer to collect it now
      // instead of sending staff to the Invoices screen for it.
      if (result?.invoice_id) {
        setPaymentPrompt({
          invoiceId: result.invoice_id,
          amount: Number(sessionPackage.price) || 0,
        });
      } else {
        onPurchased?.();
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to purchase the package."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <GenericFormDialog
        open={open}
        onClose={onClose}
        title="Sell Package"
        submitLabel="Purchase & Charge"
        fields={fields}
        initialValues={initialValues}
        isSubmitting={submitting}
        onSubmit={handleSubmit}
      />

      <PackagePaymentPromptDialog
        open={Boolean(paymentPrompt)}
        invoiceId={paymentPrompt?.invoiceId ?? null}
        amount={paymentPrompt?.amount ?? 0}
        onClose={() => {
          setPaymentPrompt(null);
          onPurchased?.();
        }}
        onPaid={() => {
          setPaymentPrompt(null);
          onPurchased?.();
        }}
      />
    </>
  );
};

export default PackagePurchaseDialog;
